/* eslint-disable @typescript-eslint/no-shadow */
import { useCallback, useState } from 'react';
import { cx } from '@linaria/core';
import { useScrollRestoration } from 'hooks/useScrollRestoration';
import { mapIdToComponent } from './cards';
import { homeErrorWrapper, homePageLayoutContent, homePageLayoutWrapper } from './HomePageLayout.styles';
import { HomePageFooter } from './components/HomePageFooter';
import { HomePageHeader } from './components/HomePageHeader';
import { ConfigureHomeCardType, ConfigureHomeContext, ConfigureHomeContextType } from './context';
import { useHomePageLayoutData } from './hooks/useHomePageLayoutData';

export function HomePageLayout() {
  const { cards, setCardsCallback } = useHomePageLayoutData();
  const [isAnimating, setIsAnimating] = useState(false);

  useScrollRestoration();

  const moveCardUp: ConfigureHomeContextType['moveCardUp'] = useCallback(
    (id) => {
      setIsAnimating(true);
      setCardsCallback((oldCards: ConfigureHomeCardType[]) => {
        const newCards: ConfigureHomeCardType[] = [...oldCards];
        const card = newCards.find((card) => card.id === id);
        if (!card) {
          return oldCards;
        }

        const prevCard = newCards.find((findCard) => findCard.configIdx === card.configIdx - 1);

        if (!prevCard) {
          return oldCards;
        }

        [card.configIdx, prevCard.configIdx] = [prevCard.configIdx, card.configIdx];

        return newCards;
      });

      setTimeout(() => {
        setCardsCallback((oldCards) => [...oldCards.map((card) => ({ ...card, idx: card.configIdx }))]);
        setIsAnimating(false);
      }, 200);
    },
    [setCardsCallback],
  );

  const moveCardTo: ConfigureHomeContextType['moveCardTo'] = useCallback(
    (id, pos) => {
      setIsAnimating(true);
      setCardsCallback((oldCards) => {
        let newCards = [...oldCards];
        const cardIdx = newCards.findIndex((card) => card.id === id);
        const oldCard = newCards[cardIdx];

        let fromPos = oldCard?.configIdx;
        let toPos = oldCard?.configIdx;
        let delta = 0;
        if (oldCard?.configIdx > pos) {
          fromPos = pos;
          toPos = oldCard?.configIdx - 1;
          delta = 1;
        } else if (oldCard?.configIdx < pos) {
          fromPos = oldCard?.configIdx + 1;
          toPos = pos;
          delta = -1;
        }

        newCards = [
          ...newCards.map((card) => {
            if (card.configIdx >= fromPos && card.configIdx <= toPos) {
              return { ...card, configIdx: card.configIdx + delta };
            }
            return card;
          }),
        ];
        newCards = [
          ...newCards.map((card) => {
            if (card.id === id) {
              return { ...card, configIdx: pos };
            }
            return card;
          }),
        ];

        return newCards;
      });

      setTimeout(() => {
        setCardsCallback((oldCards) => [...oldCards.map((card) => ({ ...card, idx: card.configIdx }))]);
        setIsAnimating(false);
      }, 200);
    },
    [setCardsCallback],
  );

  const moveCardDown: ConfigureHomeContextType['moveCardDown'] = useCallback(
    (id) => {
      setIsAnimating(true);

      setCardsCallback((oldCards) => {
        const newCards = [...oldCards];
        const card = newCards.find((card) => card.id === id);
        if (!card) {
          return oldCards;
        }

        const nextCard = newCards.find((findCard) => findCard.configIdx === card.configIdx + 1);

        if (!nextCard) {
          return oldCards;
        }

        [card.configIdx, nextCard.configIdx] = [nextCard.configIdx, card.configIdx];

        return newCards;
      });

      setTimeout(() => {
        setCardsCallback((oldCards) => [...oldCards.map((card) => ({ ...card, idx: card.configIdx }))]);
        setIsAnimating(false);
      }, 200);
    },
    [setCardsCallback],
  );

  const setCardVisibility: ConfigureHomeContextType['setCardVisibility'] = useCallback(
    (id, visible) => {
      setCardsCallback((oldCards) => {
        const newCards = oldCards.map((card) => {
          if (card.id === id) {
            return { ...card, visible };
          }
          return card;
        });
        return newCards;
      });
    },
    [setCardsCallback],
  );

  return (
    <ConfigureHomeContext.Provider value={{ cards, isAnimating, moveCardUp, moveCardTo, moveCardDown, setCardVisibility }}>
      <div className={cx(homePageLayoutWrapper, homeErrorWrapper)}>
        <HomePageHeader />
        <div className={homePageLayoutContent}>
          {cards
            .filter((card) => card.visible)
            .sort((cardA, cardB) => cardA.idx - cardB.idx)
            .map((card) => {
              const CardComponent = mapIdToComponent[card.id];
              if (!CardComponent) {
                return null;
              }
              return <CardComponent key={`card-component-${card.id}`} />;
            })}
        </div>
        <HomePageFooter />
      </div>
    </ConfigureHomeContext.Provider>
  );
}
